import React, { FunctionComponent, memo, useCallback, useEffect, useRef } from "react";
import cx from "classnames";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text";
import { AppField, ID, SimpleRecord } from "@smartsuite/types";
import { isEnterKey } from "@smartsuite/react-ui/lib/helpers/keys";

import { getDisplayValue } from "../../../../../../display-value/getFieldDisplayValue";
import { SocialNetworkField } from "./components/SocialNetworkField";
import { BaseRecord } from "../../../../../../../../../types/form";

import "./RecordItem.sass";
import { useFormConfig } from "../../../../../../../../../hooks/useFormConfig";
import { useLinkedRecordCoverImages } from "../../../../../hooks/useLinkedRecordCoverImages";
import { LinkedRecordCoverImage } from "../../LinkedRecordCoverImage/LinkedRecordCoverImage";

const COARSE_POINTER_QUERY = "(pointer: coarse)";
const MIN_FIELD_COLUMN_WIDTH_PX = 170;

interface RecordItemProps {
  coverImageField?: AppField;
  record: SimpleRecord;
  fieldsToShow: { [key: string]: AppField };
  isSelected: boolean;
  maxVisibleFields: number;
  relatedRecords?: BaseRecord[];
  isFocused: boolean;
  recordItemRef: React.RefObject<HTMLDivElement>;
  onClick: (recordId: ID) => void;
}

const RecordItemRaw: FunctionComponent<RecordItemProps> = ({
  coverImageField,
  isSelected,
  recordItemRef,
  fieldsToShow,
  record,
  isFocused = false,
  maxVisibleFields,
  relatedRecords,
  onClick,
}) => {
  const formConfig = useFormConfig();
  const refElement = useRef<HTMLDivElement>(null);
  const classes = cx("record-item", `record-item--select`, {
    "is-selected": isSelected,
    "is-focused": isFocused,
  });
  const visibleFieldSlugs = Object.keys(fieldsToShow).slice(0, maxVisibleFields);

  const coverImageFiles = useLinkedRecordCoverImages({ coverImageField, record });

  const handleClick = useCallback(() => {
    onClick(record.id);
  }, [onClick, record.id]);

  const handleEnterKey = useCallback(
    (event: React.KeyboardEvent) => {
      if (isEnterKey(event)) {
        onClick(record.id);
      }
    },
    [onClick, record.id]
  );

  useEffect(() => {
    if (isFocused) {
      refElement.current?.scrollIntoView({ block: "end" });
    }
  }, [isFocused]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      aria-label="Linked Record Item"
      className={classes}
      style={
        window.matchMedia(COARSE_POINTER_QUERY).matches
          ? {
              minWidth:
                visibleFieldSlugs.length > 2
                  ? MIN_FIELD_COLUMN_WIDTH_PX * visibleFieldSlugs.length
                  : undefined,
            }
          : undefined
      }
      ref={refElement}
      onClick={handleClick}
      onKeyDown={handleEnterKey}
    >
      {!!coverImageField && <LinkedRecordCoverImage mode="record-item" files={coverImageFiles} />}

      <div
        ref={recordItemRef}
        className={cx("record-item__content", {
          "record-item__content_cover-image": !!coverImageField,
        })}
        style={
          {
            "--visible-fields-count": `${visibleFieldSlugs.length}`,
          } as React.CSSProperties
        }
      >
        <Text className="record-item__title" wrapMode="ellipsis" weight="medium" size="l">
          {record.title || "Untitled Record"}
        </Text>

        <div className="record-item__body">
          {visibleFieldSlugs.map((fieldSlug) => {
            const value = getDisplayValue(
              record,
              fieldSlug,
              fieldsToShow,
              formConfig?.tags,
              relatedRecords
            );

            const fieldType = fieldsToShow[fieldSlug].field_type;

            if (!value) {
              return (
                <div key={fieldSlug} className="record-item__empty-field">
                  <span className="record-item__line"></span>
                </div>
              );
            }

            return (
              <div className="record-item__field" key={fieldSlug}>
                {fieldType === "socialnetworkfield" && !fieldSlug.includes(".") ? (
                  <SocialNetworkField record={record} fieldSlug={fieldSlug} />
                ) : (
                  <Text size="m" weight="regular" lineClamps={1}>
                    {value.toString()}
                  </Text>
                )}
              </div>
            );
          })}
        </div>

        <>
          <div className="record-item__icons"></div>

          <div className="record-item__corner-wrapper">
            <div className="record-item__corner">
              <div className="record-item__triangle"></div>
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export const RecordItem = memo(RecordItemRaw);
